import { useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { trackFormSubmit } from "../analytics";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Separator } from "./ui/separator";
import { Badge } from "./ui/badge";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { 
  ArrowLeft, 
  CreditCard, 
  Building2, 
  Bitcoin,
  CheckCircle,
  Lock,
  User,
  Phone,
  Mail,
  Calendar,
  ShoppingCart
} from "lucide-react";
const rebelLogo = "/assets/d85ba7a4098672812ab5e992f2ac839653942275.png";
interface CheckoutPageProps {
  onNavigateBack: () => void;
  onNavigateToPaymentConfirmation: (orderData: any) => void;
  preselectedCourse?: string;
}

export function CheckoutPage({ 
  onNavigateBack, 
  onNavigateToPaymentConfirmation,
  preselectedCourse 
}: CheckoutPageProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    correo: '',
    curso: preselectedCourse || '',
    fecha: '',
    metodoPago: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const cursos = [
    {
      id: 'online-introduccion',
      nameKey: 'checkout.courses.onlineIntroduction.name',
      price: 299,
      durationKey: 'checkout.courses.onlineIntroduction.duration',
      formatKey: 'checkout.courses.onlineIntroduction.format',
      levelKey: 'checkout.courses.onlineIntroduction.level',
      fechas: [
        { id: 'sept-18', dateKey: 'checkout.dates.sept18', timeKey: 'checkout.dates.timeOnline', available: 5 },
        { id: 'oct-15', dateKey: 'checkout.dates.oct15', timeKey: 'checkout.dates.timeOnline', available: 8 }
      ]
    },
    {
      id: 'online-avanzado',
      nameKey: 'checkout.courses.onlineAdvanced.name',
      price: 299,
      durationKey: 'checkout.courses.onlineAdvanced.duration',
      formatKey: 'checkout.courses.onlineAdvanced.format',
      levelKey: 'checkout.courses.onlineAdvanced.level',
      fechas: [
        { id: 'sept-25', dateKey: 'checkout.dates.sept25', timeKey: 'checkout.dates.timeOnline', available: 8 },
        { id: 'oct-22', dateKey: 'checkout.dates.oct22', timeKey: 'checkout.dates.timeOnline', available: 12 }
      ]
    },
    {
      id: 'bundle-online',
      nameKey: 'checkout.courses.bundleOnline.name',
      price: 499,
      durationKey: 'checkout.courses.bundleOnline.duration',
      formatKey: 'checkout.courses.bundleOnline.format',
      levelKey: 'checkout.courses.bundleOnline.level',
      originalPrice: 598,
      savings: 99,
      fechas: [
        { id: 'bundle-sept', dateKey: 'checkout.dates.bundleSept', timeKey: 'checkout.dates.timeOnline', available: 5 },
        { id: 'bundle-oct', dateKey: 'checkout.dates.bundleOct', timeKey: 'checkout.dates.timeOnline', available: 8 }
      ]
    },
    {
      id: 'presencial',
      nameKey: 'checkout.courses.presencial.name',
      price: 1499,
      durationKey: 'checkout.courses.presencial.duration',
      formatKey: 'checkout.courses.presencial.format',
      levelKey: 'checkout.courses.presencial.level',
      fechas: [
        { id: 'nov-15', dateKey: 'checkout.dates.nov15', timeKey: 'checkout.dates.timePresencial', available: 3 },
        { id: 'dic-10', dateKey: 'checkout.dates.dec10', timeKey: 'checkout.dates.timePresencial', available: 6 }
      ]
    }
  ];

  const metodosPago = [
    {
      id: 'transferencia',
      nameKey: 'checkout.paymentMethods.bankTransfer.name',
      icon: Building2,
      descriptionKey: 'checkout.paymentMethods.bankTransfer.description',
      timeKey: 'checkout.paymentMethods.bankTransfer.time'
    },
    {
      id: 'btcpay',
      nameKey: 'checkout.paymentMethods.btcpay.name',
      icon: Bitcoin,
      descriptionKey: 'checkout.paymentMethods.btcpay.description',
      timeKey: 'checkout.paymentMethods.btcpay.time'
    },
    {
      id: 'wise',
      nameKey: 'checkout.paymentMethods.wise.name',
      icon: CreditCard,
      descriptionKey: 'checkout.paymentMethods.wise.description',
      timeKey: 'checkout.paymentMethods.wise.time'
    }
  ];

  const selectedCourse = cursos.find(c => c.id === formData.curso);
  const availableFechas = selectedCourse?.fechas || [];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Reset fecha when course changes
    if (field === 'curso') {
      setFormData(prev => ({ ...prev, fecha: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));

    trackFormSubmit('checkout', selectedCourse?.id || 'unknown');
    // Navigate to payment confirmation
    onNavigateToPaymentConfirmation({
      ...formData,
      curso: selectedCourse,
      total: selectedCourse?.price || 0,
      orderId: `ORD-${Date.now()}`
    });

    setIsSubmitting(false);
  };

  const isFormValid = formData.nombre && formData.telefono && formData.correo && 
                     formData.curso && formData.fecha && formData.metodoPago;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onNavigateBack}
              className="flex items-center text-gray-600 hover:text-orange-500 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              <span>{t("checkout.header.back")}</span>
            </button>
            
            <div className="flex items-center">
              <img src={rebelLogo} alt="Rebel Genomics" className="w-8 h-8 mr-2" />
              <span className="text-xl text-gray-800">{t("checkout.header.brand")}</span>
            </div>

            <div className="flex items-center">
              <Lock className="w-4 h-4 text-green-500 mr-2" />
              <span className="text-sm text-gray-600">{t("checkout.header.securePayment")}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl mb-4 text-orange-500">
              {t("checkout.title.main")}
              <br />
              <span className="text-black">{t("checkout.title.secure")}</span>
            </h1>
            <p className="text-xl text-gray-700">
              {t("checkout.title.subtitle")}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Card className="p-8 rounded-3xl shadow-lg">
                  <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Personal Information */}
                    <div>
                      <h2 className="text-2xl mb-6 text-gray-800 flex items-center">
                        <User className="w-6 h-6 text-orange-500 mr-3" />
                        {t("checkout.form.personalInfo.title")}
                      </h2>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="nombre">{t("checkout.form.personalInfo.fullName")}</Label>
                          <Input
                            id="nombre"
                            value={formData.nombre}
                            onChange={(e) => handleInputChange('nombre', e.target.value)}
                            placeholder={t("checkout.form.personalInfo.fullNamePlaceholder")}
                            className="mt-2"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="telefono">{t("checkout.form.personalInfo.phone")}</Label>
                          <Input
                            id="telefono"
                            value={formData.telefono}
                            onChange={(e) => handleInputChange('telefono', e.target.value)}
                            placeholder={t("checkout.form.personalInfo.phonePlaceholder")}
                            className="mt-2"
                            required
                          />
                        </div>
                      </div>
                      <div className="mt-6">
                        <Label htmlFor="correo">{t("checkout.form.personalInfo.email")}</Label>
                        <Input
                          id="correo"
                          type="email"
                          value={formData.correo}
                          onChange={(e) => handleInputChange('correo', e.target.value)}
                          placeholder={t("checkout.form.personalInfo.emailPlaceholder")}
                          className="mt-2"
                          required
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Course Selection */}
                    <div>
                      <h2 className="text-2xl mb-6 text-gray-800 flex items-center">
                        <ShoppingCart className="w-6 h-6 text-orange-500 mr-3" />
                        {t("checkout.form.courseSelection.title")}
                      </h2>
                      <div>
                        <Label htmlFor="curso">{t("checkout.form.courseSelection.course")}</Label>
                        <Select value={formData.curso} onValueChange={(value: string) => handleInputChange('curso', value)}>
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder={t("checkout.form.courseSelection.coursePlaceholder")} />
                          </SelectTrigger>
                          <SelectContent>
                            {cursos.map((curso) => (
                              <SelectItem key={curso.id} value={curso.id}>
                                <div className="flex flex-col">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">{t(curso.nameKey)}</span>
                                    <span className="ml-4 text-orange-500 font-bold">
                                      {curso.originalPrice && (
                                        <span className="line-through text-gray-400 mr-2">
                                          {curso.originalPrice}€
                                        </span>
                                      )}
                                      {curso.price}€
                                    </span>
                                  </div>
                                  <div className="text-sm text-gray-600 mt-1">
                                    {t(curso.levelKey)} • {t(curso.durationKey)} • {t(curso.formatKey)}
                                  </div>
                                  {curso.savings && (
                                    <div className="text-sm text-green-600 mt-1">
                                      ¡Ahorra {curso.savings}€!
                                    </div>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Date Selection */}
                      {formData.curso && (
                        <div className="mt-6">
                          <Label htmlFor="fecha">{t("checkout.form.courseSelection.date")}</Label>
                          <Select value={formData.fecha} onValueChange={(value: string) => handleInputChange('fecha', value)}>
                            <SelectTrigger className="mt-2">
                              <SelectValue placeholder={t("checkout.form.courseSelection.datePlaceholder")} />
                            </SelectTrigger>
                            <SelectContent>
                              {availableFechas.map((fecha) => (
                                <SelectItem key={fecha.id} value={fecha.id}>
                                  <div className="flex flex-col">
                                    <span className="font-medium">{t(fecha.dateKey)}</span>
                                    <div className="text-sm text-gray-600">
                                      {t(fecha.timeKey)} • {fecha.available} {t("checkout.form.courseSelection.availableSpots")}
                                    </div>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Payment Method */}
                    <div>
                      <h2 className="text-2xl mb-6 text-gray-800 flex items-center">
                        <CreditCard className="w-6 h-6 text-orange-500 mr-3" />
                        {t("checkout.form.paymentMethod.title")}
                      </h2>
                      <RadioGroup 
                        value={formData.metodoPago} 
                        onValueChange={(value: string) => handleInputChange('metodoPago', value)}
                        className="space-y-4"
                      >
                        {metodosPago.map((metodo) => (
                          <div key={metodo.id} className="flex items-center space-x-3 p-4 border border-gray-200 rounded-xl hover:border-orange-300 transition-colors">
                            <RadioGroupItem value={metodo.id} id={metodo.id} />
                            <div className="flex items-center flex-1">
                              <metodo.icon className="w-6 h-6 text-orange-500 mr-3" />
                              <div className="flex-1">
                                <Label htmlFor={metodo.id} className="text-base font-medium cursor-pointer">
                                  {t(metodo.nameKey)}
                                </Label>
                                <p className="text-sm text-gray-600 mt-1">{t(metodo.descriptionKey)}</p>
                                <p className="text-xs text-orange-500 mt-1">{t("checkout.form.paymentMethod.processingTime")} {t(metodo.timeKey)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-6">
                      <Button
                        type="submit"
                        size="lg"
                        disabled={!isFormValid || isSubmitting}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white text-xl py-6 rounded-full disabled:opacity-50"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                            {t("checkout.form.submit.processing")}
                          </div>
                        ) : (
                          `${t("checkout.form.submit.confirm")} - ${selectedCourse?.price || 0}€`
                        )}
                      </Button>
                    </div>
                  </form>
                </Card>
              </motion.div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="sticky top-24"
              >
                <Card className="p-6 rounded-3xl shadow-lg">
                  <h3 className="text-xl mb-6 text-gray-800">{t("checkout.orderSummary.title")}</h3>
                  
                  {selectedCourse ? (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-800">{t(selectedCourse.nameKey)}</h4>
                        <div className="text-sm text-gray-600 mt-1">
                          {t(selectedCourse.levelKey)} • {t(selectedCourse.durationKey)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {t(selectedCourse.formatKey)}
                        </div>
                        {selectedCourse.savings && (
                          <Badge className="bg-green-100 text-green-800 mt-2">
                            {t("checkout.orderSummary.savings")}: {selectedCourse.savings}€
                          </Badge>
                        )}
                      </div>

                      {formData.fecha && (
                        <div>
                          <h5 className="font-medium text-gray-700">{t("checkout.orderSummary.selectedDate")}</h5>
                          <p className="text-sm text-gray-600">
                            {t(availableFechas.find(f => f.id === formData.fecha)?.dateKey || '')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {t(availableFechas.find(f => f.id === formData.fecha)?.timeKey || '')}
                          </p>
                        </div>
                      )}

                      <Separator />

                      <div className="space-y-2">
                        {selectedCourse.originalPrice && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{t("checkout.orderSummary.originalPrice")}</span>
                            <span className="line-through text-gray-400">{selectedCourse.originalPrice}€</span>
                          </div>
                        )}
                        {selectedCourse.savings && (
                          <div className="flex justify-between text-sm">
                            <span className="text-green-600">{t("checkout.orderSummary.discount")}</span>
                            <span className="text-green-600">-{selectedCourse.savings}€</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg font-bold">
                          <span>{t("checkout.orderSummary.total")}</span>
                          <span className="text-orange-500">{selectedCourse.price}€</span>
                        </div>
                      </div>

                      <div className="bg-orange-50 p-4 rounded-xl mt-6">
                        <div className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-orange-500 mr-3 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-medium text-gray-800 mb-2">{t("checkout.orderSummary.included.title")}</p>
                            <ul className="space-y-1 text-gray-600">
                              <li>{t("checkout.orderSummary.included.recording")}</li>
                              <li>{t("checkout.orderSummary.included.material")}</li>
                              <li>{t("checkout.orderSummary.included.certificate")}</li>
                              <li>{t("checkout.orderSummary.included.community")}</li>
                              {selectedCourse.id === 'presencial' && (
                                <li>{t("checkout.orderSummary.included.labMaterial")}</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">{t("checkout.orderSummary.noSelection")}</p>
                    </div>
                  )}
                </Card>

                {/* Security Info */}
                <Card className="p-4 rounded-2xl shadow-lg mt-6 bg-green-50 border-green-200">
                  <div className="flex items-center">
                    <Lock className="w-5 h-5 text-green-600 mr-3" />
                    <div className="text-sm">
                      <p className="font-medium text-green-800">{t("checkout.security.title")}</p>
                      <p className="text-green-600">{t("checkout.security.description")}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}